import React from "react";
import { DatePicker, DatePickerInput, MultiSelect, TextInput } from "carbon-components-react";
import { startCase } from "lodash";

import { ColumnDef, ColumnKind } from "../shared";
import { Operator } from "../operators";

interface ComparisonValueInputProps {
  columnDef: ColumnDef;
  operator: Operator;
  disabled: boolean;

  selectedValue: number | string;
  setValue: Function;

  selectedList: any[];
  setListValue: Function;
}

const comparisonRequiresSingleValue = operator =>
  [Operator.Equal, Operator.NotEqual, Operator.After, Operator.Before].includes(operator);

/*
 * NOTE: We want to be able to switch between "single" and "range" `datePickerType`s here.
 * Ideally and usually, we'd be able to dynamically set
 * datePickerType depending on  `comparisonRequiresSingleValue`.
 * However, "single" Datepicker behavior persists even after switching to "range", and vice versa
 * So, just conditionally switch between 2 separate <DatePicker>s for now.
 */
const renderDateTimeInput = (
  useSingleDatePicker: boolean,
  handleDateChange: Function,
  displayName: string,
  disabled: boolean,
) => {
  return (
    <>
      {useSingleDatePicker && (
        <DatePicker
          id="date-picker-start-date-single"
          datePickerType="single"
          dateFormat="Y-m-d"
          style={{ marginBottom: "10px" }}
          onChange={e => handleDateChange(e)}
        >
          <DatePickerInput
            id="date-picker-start-date-single-input"
            name="start_date_single"
            iconDescription={`Select a date value for ${displayName}`}
            labelText="Start Date *"
            placeholder="yyyy-mm-dd"
            type="text"
            size="sm"
            disabled={disabled}
          />
        </DatePicker>
      )}
      {!useSingleDatePicker && (
        <DatePicker
          id="date-picker-start-date-range"
          datePickerType="range"
          dateFormat="Y-m-d"
          style={{ marginBottom: "10px" }}
          onChange={e => handleDateChange(e)}
        >
          <DatePickerInput
            id="date-picker-start-date-range-input"
            name="start_date_range"
            iconDescription={
              useSingleDatePicker ? `Select a date value for ${displayName}` : "Select a start date"
            }
            labelText="Start Date *"
            placeholder="yyyy-mm-dd"
            type="text"
            size="sm"
            disabled={disabled}
          />
          <DatePickerInput
            disabled={disabled}
            id="date-picker-end-date-range-input"
            name="end_date_range"
            iconDescription="Select a end date"
            labelText="End Date *"
            placeholder="yyyy-mm-dd"
            type="text"
            size="sm"
          />
        </DatePicker>
      )}
    </>
  );
};

const ComparisonValueInput: React.FC<ComparisonValueInputProps> = ({
  columnDef,
  operator,
  disabled,

  selectedValue,
  setValue,

  selectedList = [],
  setListValue,
}: ComparisonValueInputProps) => {
  const { kind, displayName, jsonKey } = columnDef;

  if (kind === ColumnKind.Date || kind === ColumnKind.Time) {
    const isSingleValueComparison = comparisonRequiresSingleValue(operator);

    // Note: DatePicker's `event` value is accessed via event[0]
    const handleDateChange = isSingleValueComparison ? e => setValue(e[0]) : e => setListValue(e);

    return renderDateTimeInput(isSingleValueComparison, handleDateChange, displayName, disabled);
  }

  if (kind === ColumnKind.Enum) {
    const { enums } = columnDef;

    // Carbon requires IDs
    const enumsWithIDs = enums.map(e => ({
      ...e,
      id: e.value === null ? "null" : e.value, // special case so null/empty has a valid id
    }));

    const label = columnDef.displayName || startCase(jsonKey);

    return (
      <MultiSelect
        disabled={disabled}
        id="select-enum"
        label={selectedList.length > 0 ? "Item(s) selected" : `Select ${label}`}
        light
        items={enumsWithIDs}
        initialSelectedItems={enumsWithIDs.filter(item => selectedList.includes(item.value))}
        itemToString={item => (item ? item.label : "")}
        onChange={e => setListValue(e.selectedItems.map(i => i.value))}
      />
    );
  }

  if (kind === ColumnKind.Money) {
    return (
      <TextInput
        disabled={disabled}
        id={`${kind}-comparison-value`}
        name="comparison-value"
        value={`$${selectedValue}`}
        labelText={`Enter a currency value for "${startCase(displayName)}"`}
        placeholder="$3,400"
        type="text"
        style={{ marginBottom: "10px" }}
        invalid={false} // TODO KEVIN : Add validation for input forms
        invalidText="Enter a valid value"
        onChange={e => {
          let amount = e.target.value;

          // Remove any currency symbols and commas
          amount = amount.replace(/\$/g, "");
          amount = amount.replace(/,/g, "");

          setValue(amount);
        }}
      />
    );
  }

  if (kind === ColumnKind.Number) {
    return (
      <TextInput
        disabled={disabled}
        id={`${kind}-comparison-value`}
        name="comparison-value"
        value={selectedValue}
        labelText={`Enter a number value for "${startCase(displayName)}"`}
        placeholder="123"
        type="text"
        style={{ marginBottom: "10px" }}
        invalid={false} // TODO KEVIN : Add validation for input forms
        invalidText="Enter a valid value"
        onChange={e => setValue(e.target.value)}
      />
    );
  }

  if (kind === ColumnKind.Text) {
    return (
      <TextInput
        disabled={disabled}
        id={`${kind}-comparison-value`}
        name="comparison-value"
        value={selectedValue}
        labelText={`Enter a text value for "${startCase(displayName)}"`}
        placeholder="Enter text..."
        type="text"
        style={{ marginBottom: "10px" }}
        invalid={false} // TODO KEVIN : Add validation for input forms
        invalidText="Enter a valid value"
        onChange={e => setValue(e.target.value)}
      />
    );
  }

  return null;
};

export default ComparisonValueInput;
